import {
  getOverviewBurnRate,
  getOverviewHeadcount,
  getOverviewStaffRate,
  getStudioBurnRate,
  getStudioFinancialsByMonth,
  getStudioFinancialsByYear,
  getStudioHeadcount,
  getStudioStaffRate,
} from '@sg/services/index';
import { writeFile } from '@sg/utils/downLoadFileUtils';
import { dealDownLoadData } from '@sg/components/Chart/Operations/dealDownLoadData';
import { exportTableToCsv } from '@sg/utils/exportTableToCsv';
import { FinancialsColumns } from '../../../Studios/config';
import { exportBurnRateExcel } from '../../BurnRateCard/export/exportBurnRateExcel';
import { exportHeadcountExcel } from '../../HeadcountCard/export/exportHeadcountExcel';
import { exportStaffRateExcel } from '../../HeadcountCard/export/exportStaffRateExcel';
import { downloadFinanceData } from '../../../LiveTitles/pages/LiveFinance/utils/downloadFinance';
import type {
  TMetricsDownloadFileFormat,
  TMetricsDownloadMetricKey,
  TMetricsDownloadScope,
  TDimensionKey,
} from '../config';
import {
  PORTFOLIO_METRICS_DOWNLOAD_CONFIG,
  STUDIOS_METRICS_DOWNLOAD_CONFIG,
} from '../config';

interface IExportMetricsParams {
  metrics: TMetricsDownloadMetricKey[];
  scope: TMetricsDownloadScope;
  studioId?: string;
  studioName?: string;
  activeDateTypeSegment: string;
  startDate: string;
  endDate: string;
  fileFormat: TMetricsDownloadFileFormat;
  /** 外部页面时间筛选器的值（YYYY-MM），随请求透传给后端 */
  datatime?: string;
  /** Selected optional dimensions (e.g. by_platform) for scope-specific exports. */
  dimensions?: TDimensionKey[];
}

const buildFinancialsTableRows = (rawRows: any[], isYearMode: boolean): any[] => (
  rawRows
    .map((item: any) => ({
      date: isYearMode ? item?.year : item?.month,
      Revenue: item?.revenue,
      Profit: item?.profit,
      Profitability: item?.profitability !== null && item?.profitability !== undefined
        ? parseFloat(item.profitability.toFixed(1))
        : item?.profitability,
    }))
    .filter((item: any) => item?.date !== null && item?.date !== undefined && item?.date !== '')
    .sort((a: any, b: any) => (
      isYearMode
        ? Number(a.date) - Number(b.date)
        : a.date.localeCompare(b.date)
    ))
);

const FINANCIALS_CSV_COLUMNS = FinancialsColumns.map((col: any) => ({
  header: typeof col.header === 'string' ? col.header : (col.title || col.key),
  key: col.key,
}));

const exportTableMetricExcel = async (
  data: any[],
  columns: any[],
  name: string,
): Promise<void> => {
  await writeFile(dealDownLoadData({ data, columns, name }), name);
};

/** Financials API is year-scoped: 2025-08~2026-04 → 2025-01~2026-12. */
const toFinancialsYearBoundDates = (
  startDate: string,
  endDate: string,
): { startDate: string; endDate: string } => {
  const startYear = startDate.slice(0, 4);
  const endYear = endDate.slice(0, 4);
  return {
    startDate: `${startYear}-01`,
    endDate: `${endYear}-12`,
  };
};

const exportFinancials = async (
  params: IExportMetricsParams,
): Promise<void> => {
  if (params.scope === 'portfolio') {
    throw new Error('Portfolio Financials download is not supported');
  }
  if (!params.studioId) {
    throw new Error('Financials download requires studioId');
  }
  const { startDate, endDate } = toFinancialsYearBoundDates(
    params.startDate,
    params.endDate,
  );
  const payload = {
    studio_id: params.studioId,
    start_date: startDate,
    end_date: endDate,
  };
  const isYearMode = params.activeDateTypeSegment === 'year';
  const res = isYearMode
    ? await getStudioFinancialsByYear(payload)
    : await getStudioFinancialsByMonth(payload);
  const tableData = buildFinancialsTableRows(res?.data?.data || [], isYearMode);

  if (params.fileFormat === 'csv') {
    exportTableToCsv({
      fileName: 'Financials',
      columns: FINANCIALS_CSV_COLUMNS,
      data: tableData,
    });
    return;
  }
  await exportTableMetricExcel(tableData, FinancialsColumns, 'Financials');
};

const exportBurnRate = async (
  params: IExportMetricsParams,
): Promise<void> => {
  // Backend: download is month-granularity only; view_mode must stay by_month
  // Year UI segment does not change this payload.
  const basePayload = {
    view_mode: 'by_month' as const,
    start_date: params.startDate,
    end_date: params.endDate,
    include_breakdown: true,
  };

  if (params.scope === 'portfolio') {
    const res = await getOverviewBurnRate({
      ...basePayload,
      datatime: params.datatime ?? '',
    });
    await exportBurnRateExcel({
      // Overview returns studios[] with nested series[] month rows.
      studios: res?.data?.data ?? [],
      datatime: res?.data?.datatime ?? '',
      fileFormat: params.fileFormat,
    });
    return;
  }

  if (!params.studioId) {
    throw new Error('Burn Rate download requires studioId');
  }
  const res = await getStudioBurnRate({
    ...basePayload,
    studio_id: params.studioId,
  });
  await exportBurnRateExcel({
    rawData: res?.data?.data ?? [],
    studioName: params.studioName ?? '',
    datatime: res?.data?.datatime ?? '',
    fileFormat: params.fileFormat,
  });
};

const exportHeadcount = async (
  params: IExportMetricsParams,
): Promise<void> => {
  // Backend: download is month-granularity only; view_mode must stay by_month
  // Year UI segment does not change this payload.
  const basePayload = {
    view_mode: 'by_month' as const,
    start_date: params.startDate,
    end_date: params.endDate,
    include_breakdown: true,
  };

  if (params.scope === 'portfolio') {
    const res = await getOverviewHeadcount({
      ...basePayload,
      datatime: params.datatime ?? '',
    });
    await exportHeadcountExcel({
      // Overview returns studios[] with nested series[] month rows.
      studios: res?.data?.data ?? [],
      datatime: res?.data?.datatime ?? '',
      fileFormat: params.fileFormat,
    });
    return;
  }

  if (!params.studioId) {
    throw new Error('Headcount download requires studioId');
  }
  const res = await getStudioHeadcount({
    ...basePayload,
    studio_id: params.studioId,
  });
  await exportHeadcountExcel({
    rawData: res?.data?.data ?? [],
    studioName: params.studioName ?? '',
    datatime: res?.data?.datatime ?? '',
    fileFormat: params.fileFormat,
  });
};

const exportStaffRate = async (
  params: IExportMetricsParams,
): Promise<void> => {
  // Backend: download is month-granularity only; view_mode must stay by_month
  // Year UI segment does not change this payload.
  // Breakdown rows (items[]) are only returned when include_breakdown is set.
  const basePayload = {
    view_mode: 'by_month' as const,
    start_date: params.startDate,
    end_date: params.endDate,
    include_breakdown: true,
  };

  if (params.scope === 'portfolio') {
    const res = await getOverviewStaffRate({
      ...basePayload,
      datatime: params.datatime ?? '',
    });
    await exportStaffRateExcel({
      // Overview returns studios[] with nested series[] month rows.
      studios: res?.data?.data ?? [],
      datatime: res?.data?.datatime ?? '',
      fileFormat: params.fileFormat,
    });
    return;
  }

  if (!params.studioId) {
    throw new Error('Staff Rate download requires studioId');
  }
  const res = await getStudioStaffRate({
    ...basePayload,
    studio_id: params.studioId,
  });
  await exportStaffRateExcel({
    rawData: res?.data?.data ?? [],
    studioName: params.studioName ?? '',
    datatime: res?.data?.datatime ?? '',
    fileFormat: params.fileFormat,
  });
};

const exportFinance = async (
  params: IExportMetricsParams,
): Promise<void> => {
  await downloadFinanceData({
    startDate: params.startDate,
    endDate: params.endDate,
    byPlatform: params.dimensions?.includes('by_platform') ?? false,
    fileFormat: params.fileFormat,
  });
};

const METRIC_EXPORTERS: Record<
  TMetricsDownloadMetricKey,
  (params: IExportMetricsParams) => Promise<void>
> = {
  financials: exportFinancials,
  burnrate: exportBurnRate,
  headcount: exportHeadcount,
  staffrate: exportStaffRate,
};

const wait = (ms: number): Promise<void> => (
  new Promise(resolve => setTimeout(resolve, ms))
);

/** Thrown when one or more selected metrics fail; carries display labels for the toast. */
export class MetricsDownloadError extends Error {
  failedMetrics: string[];

  constructor(failedMetrics: string[]) {
    super(`Failed to download: ${failedMetrics.join(', ')}`);
    this.name = 'MetricsDownloadError';
    this.failedMetrics = failedMetrics;
  }
}

export const exportMetrics = async (
  params: IExportMetricsParams,
): Promise<void> => {
  // Finance has no metric checkboxes — export directly from scope.
  if (params.scope === 'finance') {
    await exportFinance(params);
    return;
  }

  // Scope config drives metric order (Studios includes Financials; Portfolio does not).
  const scopeConfig = params.scope === 'portfolio'
    ? PORTFOLIO_METRICS_DOWNLOAD_CONFIG
    : STUDIOS_METRICS_DOWNLOAD_CONFIG;
  const selectedMetrics = scopeConfig.defaultMetrics.filter(key => (
    params.metrics.includes(key)
  ));
  const labelByKey = Object.fromEntries(
    scopeConfig.metricOptions.map(option => [option.key, option.label]),
  ) as Record<TMetricsDownloadMetricKey, string>;

  // Continue after a failure so remaining metrics still download and we can list all failures.
  const failedMetrics: string[] = [];
  for (let i = 0; i < selectedMetrics.length; i += 1) {
    const metricKey = selectedMetrics[i];
    try {
      await METRIC_EXPORTERS[metricKey](params);
    } catch (error) {
      console.error(`Metrics download failed (${metricKey}):`, error);
      failedMetrics.push(labelByKey[metricKey] ?? metricKey);
    }
    // Stagger triggers so the browser does not coalesce/block multi-file downloads.
    if (i < selectedMetrics.length - 1) {
      await wait(300);
    }
  }

  if (failedMetrics.length > 0) {
    throw new MetricsDownloadError(failedMetrics);
  }
};

/** @deprecated Use exportMetrics */
export const exportStudiosMetrics = exportMetrics;
